<template>
  <div ref="rootEl">
    <ClientOnly>
      <MediaBlock
        v-if="videoUrl && isVisible"
        :id="item?.fields.id ?? `section-${item.sys.id}`"
        :cols="mappedCols"
        :media="mediaItem"
        :cols-centered="item.fields.videoCenteredInRow ?? false"
        :content-centered="item.fields.videoCentered ?? false"
        v-bind="
          $ContentfulLivePreview.getProps({
            entryId: item.sys.id,
            fieldId: 'title'
          })
        "
      />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { MediaBlock } from '@hypercodestudio/basler-components';
import { generateVideoObject } from '~/utils/generateVideoObject';
import { mapColsWidth } from '~/utils/mapColsWidth';
import { videoUrlCalculator } from '~/utils/videoUrlCalculator';
import type { IVideo } from '~~/lib/ContentfulService';
const { $ContentfulLivePreview } = useNuxtApp();

type Props = {
  item: IVideo;
};

const props = defineProps<Props>();
const rootEl = ref<Element | null>(null);
const observer = ref<IntersectionObserver | null>(null);
const isVisible = ref(false);

const videoUrl = computed(
  () =>
    videoUrlCalculator(props.item.fields) ??
    videoUrlCalculator(props.item.fields, true)
);

const mediaItem = computed(() => {
  return generateVideoObject(props.item.fields);
});

const mappedCols = computed(() => mapColsWidth(props.item.fields.videoWidth));

onMounted(() => {
  if (!rootEl.value) {
    return;
  }

  if (!('IntersectionObserver' in window)) {
    isVisible.value = true;
    return;
  }

  observer.value = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) {
        return;
      }

      isVisible.value = true;
      observer.value?.disconnect();
    },
    { rootMargin: '400px 0px' }
  );

  observer.value.observe(rootEl.value);
});

onUnmounted(() => {
  observer.value?.disconnect();
  observer.value = null;
});
</script>
